import { useState } from "react";
import { PlusCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { postOfflineCount } from "@/lib/townhall/townhall-service";

const PRESETS = [1, 11, 21, 108, 1000];

/**
 * Personal offline chant count entry — updates personal and global tallies.
 */
export function AddMyCountForm({ chantId, token, onUpdated }) {
  const [count, setCount] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (value) => {
    const n = Math.floor(Number(value));
    if (!n || n < 1) {
      setError("Enter a positive number of repetitions.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await postOfflineCount({ chantId, count: n, token });
      setMessage(`Added ${result.added} repetition${result.added === 1 ? "" : "s"} to the global saṅga.`);
      setCount("");
      onUpdated?.(result);
    } catch (e) {
      setError(e.message || "Could not save count.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white/90 border-amber-100" data-testid="add-my-count-form">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <PlusCircle className="w-5 h-5 text-amber-600" />
          Enter my chant count
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Log offline repetitions (e.g. Lalitha Namalu, Sahasranamalu) — updates your personal tally and the global counter.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p}
              type="button"
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => submit(p)}
            >
              {p}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="number"
            min={1}
            max={10000}
            placeholder="Custom count"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid="offline-count-input"
          />
          <Button type="button" disabled={loading} onClick={() => submit(count)}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
          </Button>
        </div>
        {message && <p className="text-sm text-green-700">{message}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
